import { useState, useEffect, useCallback } from "react";
import {
  FiInbox,
  FiMail,
  FiPhone,
  FiMessageSquare,
  FiCalendar,
  FiCheck,
  FiTrash2,
  FiFilter,
  FiRefreshCw,
  FiExternalLink,
} from "react-icons/fi";
import toast from "react-hot-toast";
import "./Contact.css";
import { getAllContacts, updateContactStatus, deleteContact } from "../api/api";

const statusConfig = {
  new: { label: "New", color: "#3b82f6", bg: "rgba(59, 130, 246, 0.15)" },
  pending: { label: "Pending", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.15)" },
  contaced: { label: "Contacted", color: "#8b5cf6", bg: "rgba(139, 92, 246, 0.15)" },
  done: { label: "Done", color: "#22c55e", bg: "rgba(34, 197, 94, 0.15)" },
};

const defaultStats = { total: 0, new: 0, pending: 0, contaced: 0, done: 0 };

export default function Contact() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [stats, setStats] = useState(defaultStats);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const calcStats = (contacts) => ({
    total: contacts.length,
    new: contacts.filter((r) => r.status === "new").length,
    pending: contacts.filter((r) => r.status === "pending").length,
    contaced: contacts.filter((r) => r.status === "contaced").length,
    done: contacts.filter((r) => r.status === "done").length,
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ─── Load Requests ───────────────────────────────────────────────────────────

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
const response = await getAllContacts(params);
const contacts = response?.data?.data ?? response?.data ?? [];
const serverStats = response?.data?.stats ?? response?.stats;

if (serverStats) {
  setStats({ ...defaultStats, ...serverStats });
} else {
  setStats(calcStats(contacts));
}

setRequests(contacts);

    } catch (error) {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Re-fetch whenever filter changes
  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // ─── Status Change ───────────────────────────────────────────────────────────

  const handleStatusChange = async (requestId, newStatus) => {
    console.log("Changing status to:", newStatus);
    // Optimistic update
    setRequests((prev) =>
      prev.map((r) => (r._id === requestId ? { ...r, status: newStatus } : r))
    );
    if (selectedRequest?._id === requestId) {
      setSelectedRequest((prev) => ({ ...prev, status: newStatus }));
    }

    try {
      await updateContactStatus(requestId, newStatus);
      toast.success("Status updated successfully");
    } catch (error) {
      console.error("Status change error:", error);
      console.error("Error response:", error?.response?.data);
      toast.error(error?.response?.data?.message || "Failed to update status");
      // Rollback: re-fetch fresh data from server
      loadRequests();
    }
  };

  // ─── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this request?")) return;

    try {
      await deleteContact(requestId);
      setRequests((prev) => prev.filter((r) => r._id !== requestId));
      if (selectedRequest?._id === requestId) setSelectedRequest(null);
      // Recalculate stats after deletion
      setStats((prev) => {
        const deleted = requests.find((r) => r._id === requestId);
        if (!deleted) return prev;
        return {
          ...prev,
          total: prev.total - 1,
          [deleted.status]:
            prev[deleted.status] > 0 ? prev[deleted.status] - 1 : 0,
        };
      });
      toast.success("Request deleted");
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };

  // ─── Derived state ───────────────────────────────────────────────────────────

  // If API already filters server-side, filteredRequests === requests.
  // If API returns all regardless, we filter client-side as a fallback.
  const filteredRequests =
    filter === "all" ? requests : requests.filter((r) => r.status === filter);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="requests-page">
      {/* Header */}
      <div className="requests-header">
        <div className="requests-title">
          <FiInbox size={28} />
          <div>
            <h1>Contact Requests</h1>
            <p>Manage inquiries from your website contact form</p>
          </div>
        </div>
        <button className="refresh-btn" onClick={loadRequests} disabled={loading}>
          <FiRefreshCw size={18} className={loading ? "spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="requests-stats">
        <div className="stat-card total">
          <div className="stat-icon"><FiInbox /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Requests</span>
          </div>
        </div>
        <div className="stat-card new">
          <div className="stat-icon"><FiMail /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.new}</span>
            <span className="stat-label">New</span>
          </div>
        </div>
        <div className="stat-card pending">
          <div className="stat-icon"><FiCalendar /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
        <div className="stat-card contaced">
          <div className="stat-icon"><FiPhone /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.contaced}</span>
            <span className="stat-label">Contacted</span>
          </div>
        </div>
        <div className="stat-card done">
          <div className="stat-icon"><FiCheck /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.done}</span>
            <span className="stat-label">Done</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="requests-filters">
        <div className="filter-group">
          {["all", "new", "pending", "contaced", "done"].map((f) => (
            <button
              key={f}
              className={filter === f ? "active" : ""}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="requests-loading">Loading requests...</div>
      ) : filteredRequests.length === 0 ? (
        <div className="requests-empty">
          <FiInbox size={48} />
          <h3>No requests found</h3>
          <p>
            When visitors submit your website contact form, their requests will
            appear here.
          </p>
        </div>
      ) : (
        <div className="requests-list">
          {filteredRequests.map((request) => (
            <div
              key={request._id}
              className={`request-card ${
                selectedRequest?._id === request._id ? "selected" : ""
              }`}
              onClick={() => setSelectedRequest(request)}
            >
              <div className="request-header">
                <div className="request-info">
                  <div className="request-avatar">
                    {request.fullName.charAt(0).toUpperCase()}
                  </div>
                  <div className="request-details">
                    <h4>{request.fullName}</h4>
                    <span className="request-subject">{request.subject}</span>
                  </div>
                </div>
                <span
                  className="request-status"
                  style={{
                    color: statusConfig[request.status]?.color,
                    background: statusConfig[request.status]?.bg,
                  }}
                >
                  {statusConfig[request.status]?.label}
                </span>
              </div>

              <div className="request-preview">
                <p>{request.message.substring(0, 120)}...</p>
              </div>

              <div className="request-footer">
                <div className="request-meta">
                  <span>
                    <FiMail size={14} /> {request.email}
                  </span>
                  <span>
                    <FiCalendar size={14} /> {formatDate(request.createdAt)}
                  </span>
                </div>
                <div className="request-actions">
                  <a
                    href={`mailto:${request.email}`}
                    className="action-btn"
                    title="Reply via Email"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FiExternalLink size={16} />
                    <span>Reply</span>
                  </a>
                  <button
                    className="action-btn delete"
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(request._id);
                    }}
                  >
                    <FiTrash2 size={16} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Request Detail Modal */}
      {selectedRequest && (
        <div
          className="request-modal-overlay"
          onClick={() => setSelectedRequest(null)}
        >
          <div
            className="request-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Request Details</h2>
              <button
                className="modal-close"
                onClick={() => setSelectedRequest(null)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="detail-section">
                <div className="detail-avatar">
                 
                  {selectedRequest.fullName.charAt(0).toUpperCase()}
                </div>
                <div className="detail-info">
                <h3>{selectedRequest.fullName}</h3>
                  <span
                    className="detail-status"
                    style={{
                      color: statusConfig[selectedRequest.status]?.color,
                      background: statusConfig[selectedRequest.status]?.bg,
                    }}
                  >
                    {statusConfig[selectedRequest.status]?.label}
                  </span>
                </div>
              </div>

              <div className="detail-row">
                <FiMail />
                <div>
                  <label>Email</label>
                  <a href={`mailto:${selectedRequest.email}`}>
                    {selectedRequest.email}
                  </a>
                </div>
              </div>

              <div className="detail-row">
                <FiPhone />
                <div>
                  <label>Phone</label>
                  <a href={`tel:${selectedRequest.phone}`}>
                    {selectedRequest.phone}
                  </a>
                </div>
              </div>

              <div className="detail-row">
                <FiCalendar />
                <div>
                  <label>Submitted</label>
                  <span>{formatDate(selectedRequest.createdAt)}</span>
                </div>
              </div>

              <div className="detail-row full">
                <FiMessageSquare />
                <div>
                  <label>Subject</label>
                  <span>{selectedRequest.subject}</span>
                </div>
              </div>

              <div className="detail-message">
                <label>Message</label>
                <p>{selectedRequest.message}</p>
              </div>

              <div className="detail-actions">
                <label>Change Status</label>
                <div className="status-buttons">
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <button
                      key={key}
                      className={selectedRequest.status === key ? "active" : ""}
                      onClick={() =>
                        handleStatusChange(selectedRequest._id, key)
                      }
                      style={{
                        color: config.color,
                        borderColor: config.color,
                        background:
                          selectedRequest.status === key
                            ? config.bg
                            : "transparent",
                      }}
                    >
                      {config.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <a
                  href={`mailto:${selectedRequest.email}?subject=Re: ${selectedRequest.subject}`}
                  className="reply-btn"
                >
                  <FiMail size={18} />
                  Reply via Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}